import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@rocicorp/zero/react'
import { useAuth } from '../auth/AuthContext.tsx'
import { Shell } from '../components/Shell.tsx'
import { formatCents, formatTime } from '../lib/format.ts'
import { queries } from '../zero/queries.ts'
import { AccessDeniedPage } from './AccessDeniedPage.tsx'
import { Require } from './guards.tsx'

function GuestDetailInner() {
  const { userId, guestId } = useParams({ from: '/users/$userId/guests/$guestId' })
  const navigate = useNavigate()
  const { session } = useAuth()
  const [guests, guestsResult] = useQuery(queries.guestsForUser({ userId }))
  const [drinks] = useQuery(queries.guestTab({ userId, guestId }))
  const [owner] = useQuery(queries.userById({ userId }))

  if (!session) return null
  // The subtotal is financial, so US-09's profile privacy rules apply here even
  // though the kiosk may read the guest roster for ordering.
  if (session.auth.role !== 'admin' && session.auth.sub !== userId) {
    return <AccessDeniedPage />
  }

  const guest = guests.find((candidate) => candidate.id === guestId)
  const subtotal = drinks.reduce((sum, drink) => sum + (drink.drink?.priceCents ?? 0), 0)
  const outstanding = drinks
    .filter((drink) => drink.paidAt == null)
    .reduce((sum, drink) => sum + (drink.drink?.priceCents ?? 0), 0)

  return (
    <Shell
      title={guest?.name ?? 'Guest'}
      onBack={() => navigate({ to: '/users/$userId', params: { userId } })}
    >
      {!guest ? (
        <p className="empty">
          {guestsResult.type === 'complete'
            ? 'Guest not found. A removed guest keeps its drinks on the tab but no longer has a page.'
            : 'Loading guest…'}
        </p>
      ) : (
        <>
          <div className="card">
            <div className="balance-rows">
              <div className="balance-row">
                <span className="label">Outstanding</span>
                <span>{formatCents(outstanding)}</span>
              </div>
              <div className="balance-row balance-row--total">
                <span>Subtotal</span>
                <span className="amount">{formatCents(subtotal)}</span>
              </div>
            </div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
              These charges sit on {owner?.displayName ?? 'the owning member'}'s balance.
            </p>
          </div>

          <div className="section-title">Drink history ({drinks.length})</div>
          <div className="card">
            {drinks.length === 0 ? (
              <p className="empty">No drinks recorded for this guest.</p>
            ) : (
              drinks.map((drink) => {
                const paid = drink.paidAt != null
                return (
                  <div className="line" key={drink.id}>
                    <div className="line-main">
                      <div className="line-title">
                        {drink.drink?.description ?? 'Drink'}
                        <span className={`pill-tag ${paid ? '' : 'pill-tag--owing'}`}>
                          {paid ? 'Paid' : 'Outstanding'}
                        </span>
                      </div>
                      <div className="line-sub">{formatTime(drink.purchasedAt)}</div>
                    </div>
                    <span className="line-amount">
                      {formatCents(drink.drink?.priceCents ?? 0)}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </>
      )}
    </Shell>
  )
}

export function GuestDetailPage() {
  return (
    <Require roles={['member', 'admin']} showDenied>
      <GuestDetailInner />
    </Require>
  )
}
